export type PermissionStatements = Record<string, readonly string[]>;

export type StatementAction<
  TStatements extends PermissionStatements,
  TResource extends keyof TStatements,
> = TStatements[TResource] extends readonly (infer TAction)[] ? Extract<TAction, string> : never;

export type PermissionStatementMap<TStatements extends PermissionStatements> = Partial<{
  [TResource in keyof TStatements]: readonly StatementAction<TStatements, TResource>[];
}>;

export type SingleResourcePermissionInput<
  TStatements extends PermissionStatements,
  TResource extends keyof TStatements = keyof TStatements,
> = TResource extends keyof TStatements
  ? {
      resource: TResource;
      actions: readonly StatementAction<TStatements, TResource>[];
    }
  : never;

export const platformPermissionStatements = {
  debug: ["view"],
  game: ["create", "update", "delete", "list"],
  platform_club: ["moderate", "delete"],
  session: ["list", "revoke", "delete"],
  staff: ["list", "set_role"],
  user: [
    "create",
    "list",
    "set-role",
    "ban",
    "impersonate",
    "impersonate-admins",
    "delete",
    "set-password",
    "get",
    "update",
  ],
} as const;

export const clubPermissionStatements = {
  ac: ["create", "read", "update", "delete"],
  announcement: ["create", "update", "delete"],
  ban: ["create", "remove", "list"],
  billing: ["manage"],
  club: ["update", "delete", "transfer"],
  discord: ["manage"],
  dispute: ["view", "resolve", "claim", "warn"],
  flow: ["create", "update", "delete", "set_default", "view_execution"],
  invitation: ["create", "cancel"],
  match: ["cancel"],
  member: ["create", "update", "delete", "invite", "kick", "set_role"],
  organization: ["update", "delete"],
  queue: ["create", "update", "delete", "disable"],
  team: ["create", "update", "delete"],
} as const;

export type PlatformPermissionInput = SingleResourcePermissionInput<
  typeof platformPermissionStatements
>;
export type ClubPermissionInput = SingleResourcePermissionInput<typeof clubPermissionStatements>;

export const platformRoleAdditions = {
  admin: {
    debug: ["view"],
    game: ["create", "update", "delete", "list"],
    platform_club: ["moderate", "delete"],
    staff: ["list", "set_role"],
  },
  developer: {
    debug: ["view"],
  },
  moderator: {
    debug: ["view"],
    platform_club: ["moderate"],
    user: ["ban", "list"],
  },
} satisfies Record<
  "admin" | "developer" | "moderator",
  PermissionStatementMap<typeof platformPermissionStatements>
>;

export const clubRoleAdditions = {
  admin: {
    announcement: ["create", "update", "delete"],
    ban: ["create", "remove", "list"],
    club: ["update"],
    discord: ["manage"],
    dispute: ["view", "resolve", "claim", "warn"],
    flow: ["create", "update", "delete", "set_default", "view_execution"],
    match: ["cancel"],
    member: ["invite", "kick", "set_role"],
    queue: ["create", "update", "delete", "disable"],
  },
  member: {},
  moderator: {
    announcement: ["create", "update", "delete"],
    ban: ["create", "remove", "list"],
    dispute: ["view", "resolve", "claim", "warn"],
    flow: ["view_execution"],
    match: ["cancel"],
    member: ["invite", "kick"],
  },
  owner: {
    announcement: ["create", "update", "delete"],
    ban: ["create", "remove", "list"],
    billing: ["manage"],
    club: ["update", "delete", "transfer"],
    discord: ["manage"],
    dispute: ["view", "resolve", "claim", "warn"],
    flow: ["create", "update", "delete", "set_default", "view_execution"],
    match: ["cancel"],
    member: ["invite", "kick", "set_role"],
    queue: ["create", "update", "delete", "disable"],
  },
} satisfies Record<
  "admin" | "member" | "moderator" | "owner",
  PermissionStatementMap<typeof clubPermissionStatements>
>;

export const clubPermissionBypassExclusions = {
  billing: ["manage"],
  club: ["delete", "transfer"],
} as const satisfies Partial<PermissionStatementMap<typeof clubPermissionStatements>>;

export function mergePermissionStatements<TStatements extends PermissionStatements>(
  ...sources: ReadonlyArray<PermissionStatementMap<TStatements> | undefined>
): PermissionStatementMap<TStatements> {
  const merged: Record<string, string[]> = {};

  for (const source of sources) {
    if (!source) {
      continue;
    }

    for (const [resource, actions] of Object.entries(source)) {
      if (!actions?.length) {
        continue;
      }

      if (!merged[resource]) {
        merged[resource] = [];
      }

      for (const action of actions) {
        if (!merged[resource].includes(action)) {
          merged[resource].push(action);
        }
      }
    }
  }

  return merged as unknown as PermissionStatementMap<TStatements>;
}

export function statementMapHasPermission<TStatements extends PermissionStatements>(
  statements: PermissionStatementMap<TStatements>,
  permission: SingleResourcePermissionInput<TStatements>,
): boolean {
  const allowedActions = statements[permission.resource] ?? [];
  return permission.actions.every((action) => allowedActions.includes(action));
}

export function toPermissionRecord<TStatements extends PermissionStatements>(
  permission: SingleResourcePermissionInput<TStatements>,
): Record<string, string[]> {
  return {
    [String(permission.resource)]: [...permission.actions],
  };
}
